import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export const ExaminationsApi = createApi({
  reducerPath: 'ExaminationsApi',
  baseQuery,
  tagTypes: ['Examination', 'Report'],
  endpoints: builder => ({
    getExaminations: builder.query<
      any,
      { search?: string; sessionId?: string; classId?: string; type?: string }
    >({
      query: ({ search, sessionId, classId, type }) => {
        const params = new URLSearchParams();
        if (search) {
          params.append('q', search);
        }
        if (sessionId) {
          params.append('sessionId', sessionId);
        }
        if (classId) {
          params.append('classId', classId);
        }
        if (type) {
          params.append('type', type);
        }
        return `/academics/examinations?${params.toString()}`;
      },
      providesTags: ['Examination'],
    }),
    createExamination: builder.mutation<any, any>({
      query: newExamination => ({
        url: '/academics/examinations',
        method: 'POST',
        body: newExamination,
      }),
      invalidatesTags: ['Examination'],
    }),
    updateExamination: builder.mutation<any, { id: number; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/academics/examinations/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      async onQueryStarted({ id, updates }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          ExaminationsApi.util.updateQueryData(
            'getExaminations',
            { search: '', sessionId: '' },
            draft => {
              const ExaminationsArray = draft;
              const ExaminationToUpdate = ExaminationsArray.find(
                (Examination: any) => Examination.id === id
              );
              if (ExaminationToUpdate) {
                Object.assign(ExaminationToUpdate, updates);
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'Examination', id },
        'Examination',
      ],
    }),
    deleteExamination: builder.mutation<void, number>({
      query: id => ({
        url: `/academics/examinations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        { type: 'Examination', id },
        'Examination',
      ],
    }),
    assessmentSetup: builder.mutation<any, { id: number }>({
      query: ({ id }) => ({
        url: `/academics/examinations/${id}/setup`,
        method: 'POST',
      }),
    }),
    getStudentExamReport: builder.query<
      any,
      { studentId: string; examinationId: string }
    >({
      query: ({ studentId, examinationId }) =>
        `/reports/v2/report-card?studentId=${studentId}&examinationId=${examinationId}`,
      // providesTags: ['Report'],
    }),
    getClassExamReport: builder.query<
      any,
      {
        classId: string;
        examinationId: string;
        override?: boolean;
        overrideReason?: string;
      }
    >({
      query: ({ classId, examinationId, override, overrideReason }) => {
        const params = new URLSearchParams({
          classroomId: classId,
          examinationId,
        });
        if (override) {
          params.append('override', 'true');
          params.append('overrideReason', overrideReason ?? '');
        }
        return `/reports/v2/report-cards?${params.toString()}`;
      },
      // providesTags: ['Report'],
    }),
    saveStudentRemark: builder.mutation<any, { examId: any; updates: any }>({
      query: ({ examId, updates }) => ({
        url: `/academics/examinations/${examId}/remark`,
        method: 'PATCH',
        body: updates,
      }),
      // invalidatesTags: ['Report'],
    }),
    getExamination: builder.query<any, string>({
      query: id => `/academics/examinations/${id}`,
      transformResponse: (response: any) => response?.data?.[0] ?? null,
      providesTags: (_result, _error, id) => [{ type: 'Examination', id: Number(id) }],
    }),
    unpublishExamination: builder.mutation<any, number>({
      query: id => ({
        url: `/academics/examinations/${id}/unpublish`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Examination', id },
        'Examination',
      ],
    }),
  }),
});

export const {
  useGetExaminationQuery,
  useGetExaminationsQuery,
  useCreateExaminationMutation,
  useUpdateExaminationMutation,
  useDeleteExaminationMutation,
  useAssessmentSetupMutation,
  useGetStudentExamReportQuery,
  useGetClassExamReportQuery,
  useSaveStudentRemarkMutation,
  useUnpublishExaminationMutation,
} = ExaminationsApi;
